import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import LogLayout from "../components/LogLayout";
import SectionTabs, { ANALYTICS_TABS } from "../components/SectionTabs";
import {
  BarChart3, FileText, TrendingUp, Calendar, Download,
  Building2, Package, CheckCircle2, XCircle, Clock, RefreshCw, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const formatFCFA = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0)) + " FCFA";
const COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6"];

function KpiCard({ icon: Icon, label, value, sub, color = "sky" }) {
  const colors = {
    sky: "border-sky-500/20 text-sky-400",
    emerald: "border-emerald-500/20 text-emerald-400",
    amber: "border-amber-500/20 text-amber-400",
    red: "border-red-500/20 text-red-400",
  };
  return (
    <div data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`} className={`bg-slate-900/60 border ${colors[color].split(" ")[0]} rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${colors[color].split(" ")[1]}`} />
        <span className="text-xs text-slate-400 font-medium">{label}</span>
      </div>
      <p className="text-lg font-bold text-white">{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function AIAnalysisSection({ report, onGenerate }) {
  const ai = report?.ai_analysis;
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await onGenerate(report.id);
    } finally {
      setGenerating(false);
    }
  };

  if (!ai) {
    return (
      <div data-testid="ai-section-empty" className="bg-gradient-to-br from-violet-500/5 to-sky-500/5 border border-violet-500/20 rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Analyse IA</h3>
              <p className="text-xs text-slate-500">Résumé executif et recommandations generees par Claude</p>
            </div>
          </div>
          <Button data-testid="generate-ai-btn" onClick={handleGenerate} disabled={generating}
            className="bg-violet-500 hover:bg-violet-400 text-white text-sm font-semibold rounded-lg">
            <Sparkles className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`} />
            {generating ? "Analyse en cours..." : "Générér l'analyse IA"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ai-section" className="space-y-4">
      {/* Score + Summary */}
      <div className="bg-gradient-to-br from-violet-500/5 to-sky-500/5 border border-violet-500/20 rounded-xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-violet-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-white">Analyse IA - Claude</h3>
            <p className="text-[10px] text-slate-500">
              Généré le {report.ai_generated_at ? new Date(report.ai_generated_at).toLocaleString("fr-FR") : ""}
            </p>
          </div>
          {ai.score > 0 && (
            <div className="text-center">
              <div className={`text-2xl font-black ${
                ai.score >= 75 ? "text-emerald-400" :
                ai.score >= 50 ? "text-amber-400" : "text-red-400"
              }`}>{ai.score}<span className="text-sm text-slate-500">/100</span></div>
              <p className="text-[10px] text-slate-500">Score sante</p>
            </div>
          )}
          <Button data-testid="regenerate-ai-btn" onClick={handleGenerate} disabled={generating} variant="outline" size="sm"
            className="border-violet-500/20 text-violet-400 hover:bg-violet-500/10 text-xs">
            <RefreshCw className={`w-3 h-3 mr-1 ${generating ? "animate-spin" : ""}`} />
            Regenerer
          </Button>
        </div>

        {ai.score_justification && (
          <p className="text-xs text-slate-400 mb-3 italic border-l-2 border-violet-500/30 pl-3">
            {ai.score_justification}
          </p>
        )}

        {/* Summary */}
        {ai.summary && (
          <div className="bg-slate-900/60 rounded-lg p-4 mb-4">
            <h4 className="text-xs font-semibold text-violet-400 uppercase tracking-wider mb-2">Résumé Executif</h4>
            <p className="text-sm text-slate-300 leading-relaxed">{ai.summary}</p>
          </div>
        )}

        {/* Recommendations */}
        {ai.recommendations && ai.recommendations.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-sky-400 uppercase tracking-wider mb-3">Recommandations</h4>
            <div className="space-y-2.5">
              {ai.recommendations.map((rec, i) => (
                <div key={i} className="flex gap-3 bg-slate-900/40 rounded-lg p-3 border border-white/5">
                  <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-sky-500/15 flex items-center justify-center">
                    <span className="text-sm font-bold text-sky-400">{i + 1}</span>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed pt-0.5">{rec}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReportDetail({ report, onGenerateAI }) {
  if (!report) return null;
  const inv = report.invoices || {};
  const courses = report.courses || {};
  const topIns = report.top_insurers || [];

  const pieData = [
    { name: "Payées", value: inv.paid || 0 },
    { name: "En cours", value: inv.pending || 0 },
    { name: "Rejetées", value: inv.rejected || 0 },
  ].filter(d => d.value > 0);

  const insurerData = topIns.map(i => ({
    name: i.name?.length > 12 ? i.name.substring(0, 12) + "..." : i.name,
    montant: i.amount || 0,
    recouvre: i.paid || 0,
  }));

  return (
    <div className="space-y-6" data-testid="report-detail">
      {/* AI Analysis Section */}
      <AIAnalysisSection report={report} onGenerate={onGenerateAI} />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={FileText} label="Factures" value={inv.total || 0} sub={`${formatFCFA(inv.amount_invoiced)} facture`} color="sky" />
        <KpiCard icon={TrendingUp} label="Taux recouvrement" value={`${inv.recovery_rate || 0}%`} sub={formatFCFA(inv.amount_recovered)} color="emerald" />
        <KpiCard icon={Package} label="Courses" value={courses.total || 0} sub={`${courses.completed || 0} terminees`} color="amber" />
        <KpiCard icon={XCircle} label="Rejets" value={inv.rejected || 0} sub={`${inv.pending || 0} en attente`} color="red" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Pie Chart - Invoice status */}
        {pieData.length > 0 && (
          <div className="bg-slate-900/60 border border-white/5 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Répartition des factures</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#e2e8f0" }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-2">
              {pieData.map((d, i) => (
                <div key={d.name} className="flex items-center gap-1.5 text-xs text-slate-400">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                  {d.name} ({d.value})
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Insurers Bar */}
        {insurerData.length > 0 && (
          <div className="bg-slate-900/60 border border-white/5 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Top assureurs</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={insurerData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} width={80} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#e2e8f0" }} formatter={v => formatFCFA(v)} />
                <Bar dataKey="montant" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Facture" />
                <Bar dataKey="recouvre" fill="#10b981" radius={[0, 4, 4, 0]} name="Recouvre" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Top insurers table */}
      {topIns.length > 0 && (
        <div className="bg-slate-900/60 border border-white/5 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-300 mb-3">Détail assureurs</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 border-b border-white/5">
                  <th className="text-left py-2 px-3">Assureur</th>
                  <th className="text-right py-2 px-3">Facture</th>
                  <th className="text-right py-2 px-3">Recouvre</th>
                  <th className="text-right py-2 px-3">Taux</th>
                </tr>
              </thead>
              <tbody>
                {topIns.map((ins, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-2.5 px-3 text-slate-300 font-medium">{ins.name}</td>
                    <td className="py-2.5 px-3 text-right text-slate-400">{formatFCFA(ins.amount)}</td>
                    <td className="py-2.5 px-3 text-right text-emerald-400">{formatFCFA(ins.paid)}</td>
                    <td className="py-2.5 px-3 text-right">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ins.rate >= 80 ? "bg-emerald-500/15 text-emerald-400" : ins.rate >= 50 ? "bg-amber-500/100/15 text-amber-400" : "bg-red-500/100/15 text-red-400"}`}>
                        {ins.rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LogMonthlyReportPage() {
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genMonth, setGenMonth] = useState("");
  const [genYear, setGenYear] = useState("");

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 3 }, (_, i) => currentYear - i);
  const months = [
    { value: "01", label: "Janvier" }, { value: "02", label: "Fevrier" }, { value: "03", label: "Mars" },
    { value: "04", label: "Avril" }, { value: "05", label: "Mai" }, { value: "06", label: "Juin" },
    { value: "07", label: "Juillet" }, { value: "08", label: "Aout" }, { value: "09", label: "Septembre" },
    { value: "10", label: "Octobre" }, { value: "11", label: "Novembre" }, { value: "12", label: "Decembre" },
  ];

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/log/notifications/monthly-reports`, { headers: getHeaders() });
      const list = res.data.reports || [];
      setReports(list);
      if (list.length > 0 && !selected) setSelected(list[0]);
    } catch { }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const params = {};
      if (genMonth && genYear) {
        params.month = genMonth;
        params.year = genYear;
      }
      const res = await axios.post(`${API}/log/notifications/generate-monthly-report`, params, { headers: getHeaders() });
      toast.success(res.data.message || "Rapport genere");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la generation"); }
    finally { setGenerating(false); }
  };

  const handleDownloadPdf = async (reportId) => {
    try {
      const res = await axios.get(`${API}/log/notifications/monthly-reports/${reportId}/pdf`, {
        headers: getHeaders(),
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport_mensuel_${selected?.month || "export"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (e) { toast.error("Erreur lors du téléchargément PDF"); }
  };

  const handleGenerateAI = async (reportId) => {
    try {
      const res = await axios.post(`${API}/log/notifications/monthly-reports/${reportId}/ai-analysis`, {}, { headers: getHeaders() });
      if (res.data.success) {
        toast.success("Analyse IA générée avec succès");
        // Refresh reports to get updated AI data
        const updated = await axios.get(`${API}/log/notifications/monthly-reports/${reportId}`, { headers: getHeaders() });
        if (updated.data.report) {
          setSelected(updated.data.report);
          setReports(prev => prev.map(r => r.id === reportId ? updated.data.report : r));
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la generation IA");
    }
  };

  return (
    <LogLayout title="Analytics & Prédictions" subtitle="Rapport mensuel : synthèse de performance">
      <SectionTabs tabs={ANALYTICS_TABS} />
      <div data-testid="monthly-report-page" className="space-y-6">
        {/* Header actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-sky-400" />
            <span className="text-sm text-slate-400">{reports.length} rapport(s) disponible(s)</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="gen-month" value={genMonth} onChange={e => setGenMonth(e.target.value)}
              className="bg-slate-900 border border-white/10 rounded-lg text-sm text-slate-300 px-3 py-2 focus:border-sky-500 focus:outline-none">
              <option value="">Mois actuel</option>
              {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <select data-testid="gen-year" value={genYear} onChange={e => setGenYear(e.target.value)}
              className="bg-slate-900 border border-white/10 rounded-lg text-sm text-slate-300 px-3 py-2 focus:border-sky-500 focus:outline-none">
              <option value="">Année actuelle</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <Button data-testid="generate-report-btn" onClick={handleGenerate} disabled={generating} className="bg-sky-500 hover:bg-sky-400 text-white text-sm font-semibold rounded-lg">
              <RefreshCw className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`} />
              {generating ? "Generation..." : "Générér"}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-16 bg-slate-900/40 border border-white/5 rounded-xl">
            <BarChart3 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Aucun rapport mensuel disponible</p>
            <p className="text-slate-500 text-xs mt-1">Cliquez sur "Générér maintenant" pour creer votre premier rapport</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Report List */}
            <div className="lg:col-span-1 space-y-2">
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-2 px-1">Historique</p>
              {reports.map(r => (
                <button
                  key={r.id}
                  data-testid={`report-${r.month}`}
                  onClick={() => setSelected(r)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    selected?.id === r.id
                      ? "bg-sky-500/10 border-sky-500/30 text-sky-300"
                      : "bg-slate-900/60 border-white/5 text-slate-400 hover:bg-white/5 hover:text-slate-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold capitalize">{r.month_label || r.month}</span>
                    <CheckCircle2 className={`w-3.5 h-3.5 ${selected?.id === r.id ? "text-sky-400" : "text-slate-600"}`} />
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-xs">
                    <span>{r.invoices?.total || 0} factures</span>
                    <span className="text-emerald-400">{r.invoices?.recovery_rate || 0}%</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Report Détail */}
            <div className="lg:col-span-3">
              {selected ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-white capitalize">{selected.month_label || selected.month}</h2>
                    <div className="flex items-center gap-3">
                      <Button data-testid="download-pdf-btn" onClick={() => handleDownloadPdf(selected.id)} variant="outline" className="text-xs border-sky-500/30 text-sky-400 hover:bg-sky-500/10">
                        <Download className="w-3.5 h-3.5 mr-1.5" />
                        Telecharger PDF
                      </Button>
                      <span className="text-[10px] text-slate-500">
                        {selected.pharmacy_name && `${selected.pharmacy_name} - `}
                        Généré le {new Date(selected.generated_at).toLocaleDateString("fr-FR")}
                      </span>
                    </div>
                  </div>
                  <ReportDetail report={selected} onGenerateAI={handleGenerateAI} />
                </div>
              ) : (
                <div className="flex items-center justify-center h-40 text-slate-500 text-sm">
                  Selectionnez un rapport
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </LogLayout>
  );
}
